/**
 * useJobPoller.ts — Generic job polling hook used by all three modules.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { JobView } from '@/types'

const POLL_MS = 2_000

interface PollerConfig {
  getJob: (id: string) => Promise<Record<string, unknown>>
  cancelJob: (id: string) => Promise<unknown>
}

export function useJobPoller({ getJob, cancelJob }: PollerConfig) {
  const [jobView, setJobView] = useState<JobView>({ view: 'input' })
  const pollerRef  = useRef<ReturnType<typeof setInterval> | null>(null)
  const jobIdRef   = useRef<string | null>(null)

  const stopPolling = useCallback(() => {
    if (pollerRef.current !== null) {
      clearInterval(pollerRef.current)
      pollerRef.current = null
    }
  }, [])

  const startPolling = useCallback((jobId: string) => {
    stopPolling()
    pollerRef.current = setInterval(async () => {
      try {
        const job = await getJob(jobId)
        const status = job.status as string
        if (status === 'complete') {
          stopPolling()
          setJobView({ view: 'result', jobId, meta: job as Record<string, unknown> })
        } else if (status === 'failed') {
          stopPolling()
          setJobView({ view: 'error', message: (job.error as string) ?? 'An unexpected error occurred.' })
        } else {
          setJobView({
            view: 'loading',
            jobId,
            step: (job.step as string) ?? status,
            progress: (job.progress as number) ?? 0,
          })
        }
      } catch (err) {
        console.warn('[poll] error:', err)
      }
    }, POLL_MS)
  }, [getJob, stopPolling])

  const beginJob = useCallback(async (jobId: string) => {
    jobIdRef.current = jobId
    setJobView({ view: 'loading', jobId, step: 'queued', progress: 0 })
    startPolling(jobId)
  }, [startPolling])

  const cancel = useCallback(async () => {
    stopPolling()
    if (jobIdRef.current) {
      await cancelJob(jobIdRef.current).catch(() => {})
      jobIdRef.current = null
    }
    setJobView({ view: 'input' })
  }, [cancelJob, stopPolling])

  const reset = useCallback(() => {
    stopPolling()
    jobIdRef.current = null
    setJobView({ view: 'input' })
  }, [stopPolling])

  const setError = useCallback((message: string) => {
    setJobView({ view: 'error', message })
  }, [])

  /** Open a previously completed job from history — jumps straight to result view. */
  const openJob = useCallback(async (jobId: string) => {
    try {
      const job = await getJob(jobId)
      if ((job.status as string) === 'complete') {
        stopPolling()
        jobIdRef.current = jobId
        setJobView({ view: 'result', jobId, meta: job as Record<string, unknown> })
      }
    } catch (err) {
      console.warn('[openJob] error:', err)
    }
  }, [getJob, stopPolling])

  useEffect(() => () => stopPolling(), [stopPolling])

  return { jobView, beginJob, cancel, reset, setError, openJob }
}
